/**
 * Turns a brand palette into CSS and puts it on the page.
 *
 * ── WHY A STYLESHEET AND NOT INLINE STYLES ────────────────────────────────
 * `documentElement.style.setProperty` can only write ONE set of values. The
 * palette has two — light and `[data-theme="dark"]` — and inline styles have
 * no way to express "this value, but only under that attribute". A stylesheet
 * does, and it is also a single node to remove when the theme is cleared.
 *
 * ── WHY `:root:root` ──────────────────────────────────────────────────────
 * These declarations have to beat two other `:root` blocks: the role tokens
 * in `styles/color.css` and the `@theme` block Tailwind emits from
 * `styles/theme.css`. Same specificity means document order decides, and
 * document order is not something this module can rely on — Vite injects
 * bundled CSS at times that differ between dev, HMR and a production build,
 * and a `<style>` appended in dev can end up BEFORE an HMR-reinjected
 * stylesheet.
 *
 * Repeating the selector raises specificity to (0,2,0) so the cascade decides
 * on merit rather than on load order, and `:root:root[data-theme='dark']` at
 * (0,3,0) still beats it. `!important` would achieve the same and is what
 * this kind of code usually reaches for, but it would also make these values
 * unoverridable by anything downstream, including the preview in Settings.
 */

import {
  deriveBrandPalette,
  DEFAULT_BRAND_COLOR,
  type BrandPalette,
} from './brand-palette';
import { isHexColor } from './color-space';

/** The id of the injected `<style>`, and the key the boot script reads. */
export const BRAND_STYLE_ELEMENT_ID = 'dc-brand-theme';

/**
 * Where the last applied CSS is cached.
 *
 * The CSS TEXT is cached, not the hex, so the boot script in `index.html` can
 * paint the brand before the bundle loads without shipping the colour maths
 * twice. The script is then four lines and has no idea what OKLCH is.
 */
export const BRAND_CSS_STORAGE_KEY = 'dc.brand.css';
/** The hex the cached CSS was built from, so a repaint can be skipped. */
export const BRAND_HEX_STORAGE_KEY = 'dc.brand.hex';

/**
 * Builds the `:root` declarations for a palette.
 *
 * The property list is not "every brand token" — it is exactly the tokens
 * `styles/color.css` declares for the primary role, no more. `--chart-1` and
 * `--identity-1-*` are indigo literals in that file and are deliberately NOT
 * repainted: a chart series has to stay distinguishable from its neighbours,
 * and rotating series 1 to a clinic's colour can collide it with series 2.
 * The odontogram's `--tooth-*` namespace is excluded for the reason color.css
 * gives — clinical colour is diagnostic information, not decoration.
 */
function declarations(palette: BrandPalette): string {
  const { ramp, light } = palette;
  return [
    // §1 brand ramp — styles/theme.css derives the 50–900 Tailwind scale from
    // these five anchors via color-mix, so moving them moves every
    // `*-primary-*` utility in the app without touching a call site.
    `--indigo-100:${ramp[100]}`,
    `--indigo-400:${ramp[400]}`,
    `--indigo-600:${ramp[600]}`,
    `--indigo-800:${ramp[800]}`,
    `--indigo-900:${ramp[900]}`,
    // §3 primary role.
    `--color-primary:${light.primary}`,
    `--color-primary-text:${light.primaryText}`,
    `--color-primary-hover:${light.hover}`,
    `--color-primary-active:${light.active}`,
    `--color-primary-soft:${light.soft}`,
    `--color-primary-border:${light.border}`,
    `--color-primary-on:${light.on}`,
    // The pre-React loader in index.html paints with this one. Kept in sync
    // so a hard refresh does not flash the Dentacares pine spinner inside
    // an otherwise branded shell.
    `--boot-primary:${light.primary}`,
  ].join(';');
}

function darkDeclarations(palette: BrandPalette): string {
  const { dark } = palette;
  // Only the role tokens, matching color.css §7 — which overrides
  // `--color-primary-*` in dark but leaves the `--indigo-*` ramp alone.
  // Diverging from that here would make the two files disagree about what
  // dark mode is, in a theme that is still dormant and so would not be caught
  // by looking at it.
  return [
    `--color-primary:${dark.primary}`,
    `--color-primary-text:${dark.primaryText}`,
    `--color-primary-hover:${dark.hover}`,
    `--color-primary-active:${dark.active}`,
    `--color-primary-soft:${dark.soft}`,
    `--color-primary-border:${dark.border}`,
    `--color-primary-on:${dark.on}`,
  ].join(';');
}

/** The full stylesheet text for a brand colour. */
export function buildBrandCss(hex: string): string {
  const palette = deriveBrandPalette(hex);
  return (
    `:root:root{${declarations(palette)}}` +
    `:root:root[data-theme='dark']{${darkDeclarations(palette)}}`
  );
}

function styleElement(): HTMLStyleElement | null {
  if (typeof document === 'undefined') return null;
  const existing = document.getElementById(BRAND_STYLE_ELEMENT_ID);
  if (existing instanceof HTMLStyleElement) return existing;

  const created = document.createElement('style');
  created.id = BRAND_STYLE_ELEMENT_ID;
  document.head.appendChild(created);
  return created;
}

/**
 * The address-bar / task-switcher colour. A themed dashboard whose browser
 * chrome is still the Dentacares pine looks like two different apps on a phone, which is
 * where this product is most often used at the chairside.
 */
function syncMetaThemeColor(hex: string): void {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (meta) meta.content = hex;
}

/**
 * Applies a brand colour to the document, or clears it for `null`.
 *
 * Returns the colour actually applied, so a caller can tell a rejected value
 * from an accepted one without re-validating.
 */
export function applyBrandColor(hex: string | null): string | null {
  const element = styleElement();
  if (!element) return null;

  if (!hex || !isHexColor(hex)) {
    // Emptying rather than removing the node: the node's presence is what
    // stops a second `<style>` being appended on the next call, and an empty
    // stylesheet is exactly "no override", which is what the Dentacares
    // default is — color.css already holds those values.
    element.textContent = '';
    syncMetaThemeColor(DEFAULT_BRAND_COLOR);
    clearCachedBrandCss();
    return null;
  }

  const normalized = hex.toLowerCase();
  const css = buildBrandCss(normalized);
  // Writing identical text still invalidates style on some engines, and this
  // runs on every auth refresh and every settings save.
  if (element.textContent !== css) element.textContent = css;
  syncMetaThemeColor(normalized);
  cacheBrandCss(normalized, css);
  return normalized;
}

/**
 * Applies a colour WITHOUT caching it — the live preview in Settings.
 *
 * A preview that wrote through to storage would survive a refresh as though
 * it had been saved, which is the difference between "try this" and "ship
 * this to every member of the clinic".
 */
export function previewBrandColor(hex: string | null): void {
  const element = styleElement();
  if (!element) return;
  if (!hex || !isHexColor(hex)) {
    element.textContent = '';
    return;
  }
  const css = buildBrandCss(hex.toLowerCase());
  if (element.textContent !== css) element.textContent = css;
}

function cacheBrandCss(hex: string, css: string): void {
  try {
    localStorage.setItem(BRAND_HEX_STORAGE_KEY, hex);
    localStorage.setItem(BRAND_CSS_STORAGE_KEY, css);
  } catch {
    // Private browsing, a full quota, or blocked site data. The theme still
    // applies for this session; only the flash-free first paint is lost.
  }
}

export function clearCachedBrandCss(): void {
  try {
    localStorage.removeItem(BRAND_HEX_STORAGE_KEY);
    localStorage.removeItem(BRAND_CSS_STORAGE_KEY);
  } catch {
    /* see cacheBrandCss */
  }
}

/** The colour the boot script painted, if any. */
export function readCachedBrandHex(): string | null {
  try {
    const hex = localStorage.getItem(BRAND_HEX_STORAGE_KEY);
    return isHexColor(hex) ? hex : null;
  } catch {
    return null;
  }
}
